import { FC } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import styled, { useTheme, withTheme } from 'styled-components'
import { Routes } from '@/core/routes'
import SlideMenuAdditionalInfo from '@/components/organisms/slideMenuAdditionalInfo'
import SwiperMenuAdd from '@/components/organisms/swiperMenuAdd'
import { useAdditionalInfoProvider } from '@/providers/additionalInfoProvider'
import { Asset, useAsset } from '@/providers/assetsProvider'
import LoginButton from '@/components/molecules/loginButton'
import MobileHeaderButton from '@/components/molecules/mobileHeaderButton'
import { WishlistIconDefault } from '@/components/atoms/icons/WishlistIconDefault'
import { CartIconNewDefault } from '@/components/atoms/icons/CartIconNewDefault'
import { FocusMode, useCurrentPage } from '@/providers/focusModeProvider'
import { useOrder } from '@/providers/order/old/orderProvider'
import { MenuDrawerDefault } from '@/components/atoms/icons/MenuDrawerDefault'
import ToolbarLinkLogo from '@/components/molecules/toolbarLinkLogo'
import ProductSearchAi from '@/components/molecules/aiSearch/productSearchAi'
import { validFor } from '@/theme/validFor'
import useDevice from '../../../../hooks/useDevice'
import { IconButton } from '@/components/molecules/iconButtons'

type DesktopToolbarRedesignProps = {
  wishlistProductCount: number
  onDrawerClick?: () => void
}

const DesktopToolbarRedesign: FC<DesktopToolbarRedesignProps> = ({
  wishlistProductCount,
  onDrawerClick
}) => {
  const { getFocusType } = useCurrentPage()
  const { show, showAdditionalMenu, showAdditionalMenuOnHP } =
    useAdditionalInfoProvider()
  const { count } = useOrder()
  const theme = useTheme()
  const display = getFocusType() === FocusMode.Basic ? 'none' : 'inherit'
  const { isCartProcessStep } = useAsset()

  const isSearchbarOpen = !isCartProcessStep
  const { isTablet } = useDevice()

  return (
    <StyledFlex isSearchbarOpen={isSearchbarOpen}>
      {isTablet && isSearchbarOpen ? (
        <>
          <Flex flexDirection="row">
            <StyledIconButton
              onClick={onDrawerClick}
              title={''}
              IconElement={
                <MenuDrawerDefault
                  color={theme.colors.onBackground}
                  data-testid="mobileHeaderToolbar"
                />
              }
              display={display}
            />

            <ToolbarLinkLogo
              imgAsset={Asset.InvertedLogo}
              ml={display ? 0 : 20}
              data-testid="headerMobileLogo"
            />
          </Flex>

          <SearchWrapper isCartProcessStep={isCartProcessStep}>
            <ProductSearchAi />
          </SearchWrapper>
        </>
      ) : null}
      <Flex
        flexDirection="row"
        alignItems="center"
        height={29}
        data-testid="mobileHeaderButtons"
      >
        <LoginButton newSearch={true} color={theme.colors.onBackground} />
        <MobileHeaderButton
          data-testid={Routes.wishlist}
          hrefLink={Routes.wishlist}
          IconElement={
            <WishlistIconDefault color={theme.colors.onBackground} />
          }
          notifyCount={wishlistProductCount}
          display={display}
        />
        <HeaderButtonWrapper>
          <MobileHeaderButton
            data-testid={Routes.cart}
            hrefLink={Routes.cart}
            IconElement={
              <CartIconNewDefault color={theme.colors.onBackground} />
            }
            notifyCount={count}
          />
        </HeaderButtonWrapper>
        {show && <SlideMenuAdditionalInfo />}
        {showAdditionalMenu || showAdditionalMenuOnHP ? (
          <SwiperMenuAdd color={theme.colors.onBackground} />
        ) : null}
      </Flex>
    </StyledFlex>
  )
}

export default withTheme(DesktopToolbarRedesign)

const StyledFlex = styled(Flex)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease-out;
  height: 90px;
  ${validFor.tablet` 
    background-color: ${(props) => props.theme.colors.background};
  `}
`

const HeaderButtonWrapper = styled(Box)`
  position: relative;
  z-index: 0;
`

const StyledIconButton = styled(IconButton)`
  border: 1px solid ${(props) => props.theme.colors.background};
`

const SearchWrapper = styled.div`
  justify-content: stretch;
  ${validFor.desktop` 
    min-width: 500px;
  `}

  ${validFor.tablet` 
    min-width: 400px;
    max-width: 450px;
  `}
  display: ${(props) => (props.isCartProcessStep ? 'none' : 'flex')};
`
